import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import type {
  AuthSession,
  LoginRequest,
  PublicUser,
  RegisterRequest,
} from "@nh/shared";
import {
  AuthContext,
  type AuthContextValue,
  type AuthStatus,
} from "./auth.contextValue";
import {
  clearSession,
  onSessionCleared,
  refreshSession,
  setAccessToken,
} from "@/shared/api/session";
import { useRunOnce } from "@/shared/lib/useRunOnce";
import { PulseLoader } from "@/shared/ui/core/PulseLoader/PulseLoader";
import { googleAuthRequest, loginRequest, logoutRequest, registerRequest } from "./auth.api";

/*
 * `AuthStatus`, `AuthContextValue`, el contexto y `useAuth` se mudaron a
 * `auth.contextValue.ts`. NO los traigas de vuelta: este archivo exporta
 * un COMPONENTE, y tener el `createContext` al lado de un componente es
 * lo que rompía la recarga en caliente. La explicación entera está en
 * ese archivo.
 *
 * Se reexportan desde aquí para no romper los once sitios que ya
 * importaban de esta ruta.
 */
export {
  AuthContext,
  useAuth,
  type AuthContextValue,
  type AuthStatus,
} from "./auth.contextValue";

export interface AuthProviderProps {
  children: ReactNode;
  /**
   * Qué pintar mientras se intenta restaurar la sesión al cargar la app.
   * Por defecto un velo neutro: NUNCA el login. Enseñar el login medio
   * segundo y luego cambiarlo a la app es el parpadeo que Anahí no quiere.
   */
  fallback?: ReactNode;
}

export function AuthProvider({ children, fallback }: AuthProviderProps) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");

  const adopt = useCallback((session: AuthSession): PublicUser => {
    setAccessToken(session.accessToken);
    setUser(session.user);
    setStatus("authenticated");
    return session.user;
  }, []);

  // El bootstrap corre UNA vez, aunque StrictMode monte el efecto dos.
  // Sin bandera `alive`: `useRunOnce` documenta por qué la combinación de
  // candado + bandera cuelga la app en StrictMode.
  useRunOnce(() => {
    refreshSession()
      .then(adopt)
      .catch(() => {
        // Sin cookie válida, con el backend caído o con la red muerta: se
        // arranca anónimo. No es un error que mostrar — es lo normal la
        // primera vez que alguien abre la app.
        setUser(null);
        setStatus("anonymous");
      });
  });

  /**
   * El cliente de API llama a `clearSession()` cuando el servidor dice
   * `TOKEN_INVALID`. Aquí se refleja en el estado de React, para que
   * ninguna pantalla tenga que acordarse de hacerlo.
   */
  useEffect(
    () =>
      onSessionCleared(() => {
        setUser(null);
        setStatus("anonymous");
      }),
    [],
  );

  const login = useCallback(
    async (input: LoginRequest) => adopt(await loginRequest(input)),
    [adopt],
  );

  /*
   * Entrar con GOOGLE sí te deja dentro (es un LOGIN, no un registro):
   * el credential del botón oficial abre la misma sesión que el login
   * con contraseña.
   */
  const loginWithGoogle = useCallback(
    async (credential: string) => adopt(await googleAuthRequest(credential)),
    [adopt],
  );

  /*
   * ── Registrarse NO te deja dentro (Anahí, 26-ago) ──────────────────
   * El flujo es el clásico: crear la cuenta → pantalla de login →
   * entrar con tus credenciales. El servidor SÍ abre una sesión al
   * registrar (deja la cookie de refresh), así que aquí se cierra en el
   * acto: si solo se ignorara, el siguiente arranque de la app la
   * restauraría y la persona aparecería dentro sin haber iniciado
   * sesión nunca.
   *
   * Si el cierre falla (red), no se rompe el registro: la cuenta ya
   * existe, y la cookie huérfana caduca sola.
   */
  const register = useCallback(async (input: RegisterRequest) => {
    const session = await registerRequest(input);
    try {
      await logoutRequest();
    } catch {
      // La cuenta quedó creada; la sesión del servidor caducará sola.
    }
    return session.user;
  }, []);

  const restore = useCallback(async () => adopt(await refreshSession()), [adopt]);

  const logout = useCallback(async () => {
    try {
      // Que el backend borre la cookie httpOnly y revoque el refresh.
      await logoutRequest();
    } catch {
      // Si el servidor no responde, la sesión local se cierra igual: dejar
      // a alguien "dentro" porque el API falló es peor que cerrarle.
    } finally {
      clearSession();
      setUser(null);
      setStatus("anonymous");
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      isAuthenticated: status === "authenticated",
      login,
      loginWithGoogle,
      register,
      logout,
      restore,
    }),
    [user, status, login, loginWithGoogle, register, logout, restore],
  );

  return (
    <AuthContext.Provider value={value}>
      {status === "loading" ? (fallback ?? <AuthBootstrapVeil />) : children}
    </AuthContext.Provider>
  );
}

/**
 * Velo de arranque. Deliberadamente sobrio: el mismo fondo cálido de la
 * app y un punto que late. Nada que se parezca a una pantalla real, para
 * que si tarda 200 ms no parezca que algo cambió de sitio.
 */
function AuthBootstrapVeil() {
  return (
    <PulseLoader label="Restaurando tu sesión…" className="min-h-screen bg-ds-cream" />
  );
}
